/**
 * What follows from what the operator just did.
 *
 * This file is where "Good call!" would have gone, and the reason it did not.
 *
 * An assistant that congratulates an operator on closing a runway is praising a
 * decision whose outcome is not known yet — not by her, not by anyone, not
 * until the re-test comes back. On an airfield that is worse than useless: an
 * operator who is told they did well stops looking, and an assistant whose
 * approval can be earned is an assistant people start deciding *for*. The
 * platform already refuses to print a KPI it cannot compute rather than show a
 * confident zero; the same rule applies to judgement.
 *
 * So she reports consequences instead. "Three work orders were waiting on this
 * one and are now released" is useful, checkable, and true at the moment she
 * says it. It is also the thing an operator actually wants at that moment,
 * which praise is not.
 *
 * The hard rule below, enforced by a test: every line she produces here must be
 * derivable from the event and the counts handed in. When nothing is derivable
 * she returns null and says nothing at all, because filler in this position
 * teaches people to skim exactly the surface that will one day carry something
 * that matters.
 */
import type { ActionEvent, Consequence, Source } from "./types";

export interface ConsequenceContext {
  /** Site code, used only to label the source. */
  siteCode: string;
  /** Work orders that were waiting on the one just moved, if the caller knows. */
  unblocked?: readonly string[];
  /** Open work orders remaining at this site after the action, if the caller knows. */
  openWorkOrders?: number;
  /** Open alerts remaining after the action, if the caller knows. */
  openAlerts?: number;
}

/*
 * The platform's own status vocabulary, not a second one invented here.
 * "Cancelled" is deliberately not in the finished set: the order is terminal
 * but the work did not happen, so anything that was waiting on it is not
 * released — it is stranded, which is the opposite thing to tell an operator.
 */
const FINISHED = new Set(["completed", "verified"]);
const STARTED = new Set(["dispatched", "in_progress"]);

const label = (id: string, title?: string) => (title ? `${id} · ${title}` : id);

function localSource(ctx: ConsequenceContext, detail: string): Source {
  return { kind: "local", label: `This platform · ${ctx.siteCode}`, detail };
}

/**
 * Lines that are true of any recorded action, and say something the operator
 * benefits from knowing: the log names them, not the assistant. That is the
 * whole guarantee this module rests on, so it is worth stating where it lands.
 */
function attribution(): string {
  return "Recorded against you in the transition log — the assistant is not an actor here.";
}

export function consequenceOf(ev: ActionEvent, ctx: ConsequenceContext): Consequence | null {
  const follows: string[] = [];
  const sources: Source[] = [];

  switch (ev.kind) {
    case "work-order-transition": {
      const to = ev.to.toLowerCase();
      if (to === "cancelled") {
        follows.push(`${label(ev.id, ev.title)} cancelled — the work was not done.`);
        if (ctx.unblocked && ctx.unblocked.length > 0) {
          follows.push(
            `${ctx.unblocked.length} work order${ctx.unblocked.length === 1 ? " was" : "s were"} waiting on it: ${ctx.unblocked.join(", ")}. Cancelling does not release ${ctx.unblocked.length === 1 ? "it" : "them"}.`,
          );
          sources.push(localSource(ctx, `${ctx.unblocked.length} dependent work order(s)`));
        }
      } else if (FINISHED.has(to)) {
        follows.push(`${label(ev.id, ev.title)} moved ${ev.from} → ${ev.to}.`);
        if (ctx.unblocked && ctx.unblocked.length > 0) {
          follows.push(
            ctx.unblocked.length === 1
              ? `${ctx.unblocked[0]} was waiting on it and is now released.`
              : `${ctx.unblocked.length} work orders were waiting on it and are now released: ${ctx.unblocked.join(", ")}.`,
          );
          sources.push(localSource(ctx, `${ctx.unblocked.length} dependent work order(s)`));
        }
        if (typeof ctx.openWorkOrders === "number") {
          follows.push(
            ctx.openWorkOrders === 0
              ? "Nothing else is open at this site."
              : `${ctx.openWorkOrders} work order${ctx.openWorkOrders === 1 ? "" : "s"} still open here.`,
          );
          sources.push(localSource(ctx, "open work-order count"));
        }
      } else if (STARTED.has(to)) {
        follows.push(`${label(ev.id, ev.title)} is now ${ev.to}.`);
      } else {
        follows.push(`${label(ev.id, ev.title)} moved ${ev.from} → ${ev.to}.`);
      }
      break;
    }

    case "work-order-created": {
      follows.push(
        ev.priority ? `${label(ev.id, ev.title)} raised at ${ev.priority}.` : `${label(ev.id, ev.title)} raised.`,
      );
      if (typeof ctx.openWorkOrders === "number") {
        follows.push(`${ctx.openWorkOrders} now open at this site.`);
        sources.push(localSource(ctx, "open work-order count"));
      }
      break;
    }

    case "alert-acknowledged": {
      follows.push(`${label(ev.id, ev.title)} acknowledged.`);
      if (typeof ctx.openAlerts === "number") {
        follows.push(
          ctx.openAlerts === 0
            ? "No alerts remain open here."
            : `${ctx.openAlerts} alert${ctx.openAlerts === 1 ? "" : "s"} still open.`,
        );
        sources.push(localSource(ctx, "open alert count"));
      }
      break;
    }

    case "inspection-closed": {
      follows.push(`${label(ev.id, ev.title)} closed.`);
      if (typeof ev.failures === "number" && ev.failures > 0) {
        follows.push(
          `${ev.failures} item${ev.failures === 1 ? "" : "s"} failed on it — those carry forward, closing the sheet does not close them.`,
        );
        sources.push(localSource(ctx, "inspection result"));
      }
      break;
    }

    case "rule-verified": {
      follows.push(
        ev.passed
          ? `${label(ev.id, ev.title)} verified — the evidence satisfies it as at now.`
          : `${label(ev.id, ev.title)} did not verify. The finding stands.`,
      );
      break;
    }

    default:
      return null;
  }

  if (follows.length === 0) return null;
  follows.push(attribution());

  return {
    title: "What follows",
    follows,
    sources: sources.length ? sources : undefined,
  };
}

/**
 * The words she must never produce here, kept beside the code they constrain
 * rather than only in the test, so that anyone adding a case can see the line
 * they are not allowed to cross.
 */
export const EVALUATIVE_WORDS = [
  "good call",
  "great decision",
  "well done",
  "nice work",
  "excellent",
  "perfect",
  "smart",
  "right move",
  "the right call",
] as const;
